import { OutdoorType, CreativeSize } from 'core/creative/Creative';
import { AdType } from 'core/rtbCampaign/RtbCampaign';

export type BidPriceSetting = {
  cpc?: number;
  cpm?: number;
  cpv?: number;
  vCpm?: number;
};

export type CampaignBidPriceSetting =
  | {
      tmpId?: undefined;
      type: string;
      adTypeId?: undefined;
      autoBidCap: BidPriceSetting;
      bidFloor: BidPriceSetting;
    }
  | {
      tmpId?: number;
      type: string;
      adTypeId?: number;
      autoBidCap: BidPriceSetting;
      bidFloor: BidPriceSetting;
    };

export enum ChannelGroupStatus {
  ACTIVE = 1,
  ARCHIVED
}

/**
 * ECOPD-5701：渠道列表的狀態篩選。字面值是送給後端的 query 值，改動等於改契約。
 * 只看 `effectiveArchived`，不分來源（RMax 單方封存也算 ARCHIVED）。
 *
 * ⚠️ 後端預設是 ALL，「預設顯示啟用中」由畫面自己帶 ACTIVE 達成。見
 * `.claude/context/adr/0003-channel-list-status-filter-defaults-to-all.md`。
 */
export enum ChannelGroupStatusFilter {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
  ALL = 'all'
}

export type DynamicCreativeTypeSetting = {
  bannerTypeId: number;
  name: string;
  minDuration: number;
  maxDuration?: number;
  width: number;
  height: number;
  supportedOutdoorTypes: OutdoorType[];
  channelGroupId: number;
};

export enum DeliveryUnit {
  STORE = 'STORE',
  DEVICE = 'DEVICE'
}

export enum CampaignAdTypeState {
  ACTIVE = 1,
  INACTIVE,
  DELETED = 99
}

export type DynamicCampaignAdTypeSetting = {
  adTypeId: number;
  adType?: AdType;
  name: string;
  nameI18n: NameI18n;
  pricingModel: string[];
  bannerTypes: DynamicCreativeTypeSetting[];
  channelGroupId: number;
  state: CampaignAdTypeState;
  screenDisplayType?: ScreenDisplayType;
};

export type CampaignAdTypeSettingFormData = Omit<
  DynamicCampaignAdTypeSetting,
  'adTypeId' | 'bannerTypes' | 'channelGroupId' | 'state'
> & {
  tmpId?: number;
  adTypeId?: number;
  bannerTypes: Omit<
    DynamicCreativeTypeSetting,
    'bannerTypeId' | 'name' | 'channelGroupId'
  >[];
  state?: CampaignAdTypeState;
};

export enum ChannelType {
  RTB = 'RTB',
  BEACON = 'BEACON',
  OUTDOOR = 'OUTDOOR',
  MOVABLE_OUTDOOR = 'MOVABLE_OUTDOOR',
  // ECOPD-5581: 電子紙。後端由 RMax 的 channelGroupSalesType='epaper' 對應而來。
  // 字面值必須逐字是 'E_PAPER'（跨 repo 比對的 wire 值），改動等於改契約。
  E_PAPER = 'E_PAPER',
  OTHERS = 'OTHERS'
}

/**
 * ECOPD-5722：連台渠道的合成設定是否可用，由後端算好送來。
 * 字面值是跨 repo 比對的 wire 值，改動等於改契約。
 *
 * 前端一律照讀，禁止自己用 compositePixel 與 screenPixel 重算 ——
 * 兩份定義一旦漂移，症狀是渠道頁說設定正常、建立素材卻被後端擋下來。
 */
export enum CompositeSettingStatus {
  VALID = 'VALID',
  NOT_SYNCED = 'NOT_SYNCED',
  SIZE_MISSING = 'SIZE_MISSING',
  SIZE_NOT_MULTIPLE = 'SIZE_NOT_MULTIPLE',
  SIZE_TOO_SMALL = 'SIZE_TOO_SMALL'
}

export type ScreenProperty = {
  isScreenSpliced: boolean;
  isMultiScreenSameVideo: boolean;
  screenSplicedVerticalScreenCount: number;
  screenSplicedHorizontalScreenCount: number;
  screenPixelWidth: number;
  screenPixelHeight: number;
  /**
   * ECOPD-5722：要不要把切割後的素材合成回一支影片。值由 RMax 決定，PMax 唯讀。
   *
   * ⚠️ 這是三態：`true` / `false` / 不存在。不存在代表 RMax 還沒同步這個欄位
   * （舊渠道），要當成「設定有誤」擋下來；`false` 是正常的「不要合成」，照常運作。
   * 宣告成必填布林、或在任何地方寫 `?? false`，都會把這兩者壓成同一個值，
   * 而且不會有測試變紅。
   */
  isCompositeCreative?: boolean;
  /** ECOPD-5722：合成後實際播放的寬。isCompositeCreative 不是 true 時 RMax 不提供。 */
  compositePixelWidth?: number;
  /** ECOPD-5722：合成後實際播放的高。同上。 */
  compositePixelHeight?: number;
  /**
   * ECOPD-5722：後端算好的合成設定判定結果。
   * 只有 outdoor 的連台渠道才有；其他渠道整個欄位不存在。
   */
  compositeSettingStatus?: CompositeSettingStatus;
};

/**
 * 電子紙渠道專屬的欄位組（ECOPD-5581），掛在 `property.epaperProps`。
 *
 * `siteId` 是唯讀的——值只由 RMax 同步寫入。畫面上不給編輯還不夠：wrapForServer
 * 會把 Formik 的 initialValues 原樣送回去，所以送出前要明確把它拿掉。
 */
export type EpaperProps = {
  siteId?: string | null;
  epaperDefaultImageUrl?: string | null;
};

/**
 * 戶外渠道的品牌設定，掛在 `property.addonProps.brand`。
 *
 * 地圖選門市時，品牌名稱會組進 Google Places 的文字搜尋，兩張圖示取代系統預設的 pin。
 * 每個欄位都是選填：後端把 trim 後為空的值存成 null，所以讀取端要同時接受
 * 缺 key、null 與空字串。
 */
export type ChannelGroupBrand = {
  nameI18n?: Partial<NameI18n> | null;
  url?: string | null;
  // 「符合篩選」狀態的 pin
  filteredIconUrl?: string | null;
  // 「未符合」與「已滿」狀態的 pin；「已選取」一律用系統的紅色 pin
  normalIconUrl?: string | null;
};

export type ChannelGroupDTO = {
  id: number;
  channel: string;
  name: string;
  nameI18n: NameI18n;
  publisherIds: string[];
  alertEmails: string[];
  status: ChannelGroupStatus;
  // ECOPD-5572: PMax 自停旗標。只由 /channel-groups/{id}/archive、/unarchive 改變，
  // RMax 同步永遠不碰它。前端**只**拿它決定按鈕要顯示「停用」還是「重新啟用」。
  // 判斷渠道是不是停用一律看 effectiveArchived，不要看這個。
  pmaxArchived?: boolean;
  // ECOPD-5572: 合併判定（RMax 已封存 OR PMax 自停），由**後端單一計算**後送來。
  // 前端一律照讀，**禁止**自己寫 `status === ARCHIVED || pmaxArchived` 重算 ——
  // 兩份定義一旦漂移，症狀是某些頁面擋、某些頁面不擋，而且沒有任何測試會紅。
  //
  // 宣告成 optional 是因為既有測試 fixture 沒有這個欄位；後端實際上一定會送。
  // 正規化成布林只在 `ChannelGroupSetting.register` 一處做。
  effectiveArchived?: boolean;
  // ECOPD-5701: 停用理由，依來源分開儲存、可同時有值。
  //
  // ⚠️ 後端只在該側確實停用時才輸出，沒有理由時整個 key 不存在（不是 null）。
  // 判定一律用 truthy，**不要**寫 `=== null`。
  // 「已停用但沒有理由」是合法狀態（舊渠道不 backfill），不得顯示成錯誤。
  pmaxArchiveReason?: string;
  rmaxArchiveReason?: string;
  channelType: ChannelType;
  addonFeatures: {
    report: {
      [key: string]: boolean;
    };
    campaign: {
      [key: string]: boolean;
    };
  };
  property: {
    addonProps: {
      campaignBidPrice: CampaignBidPriceSetting[];
      currency: string;
      outdoorCreativeSizes: CreativeSize[];
      defaultMargin: number;
      nonExpressPassCategories: string[];
      brand?: ChannelGroupBrand | null;
    };
    // ECOPD-5581: 電子紙渠道專屬的欄位組，讀寫共用同一個形狀。
    // 放在 property 底下而不是攤在頂層，是因為 siteId 這種名字攤開來看不出它是
    // 電子紙專屬的——語境由結構提供，不必靠命名前綴。非電子紙渠道不會有這一組。
    epaperProps?: EpaperProps;
  };
  campaignAdTypeSettings: DynamicCampaignAdTypeSetting[];
  deliveryUnit?: DeliveryUnit;
  // ...連台相關設定
  screenProperty?: ScreenProperty;
  // ECOPD-5470: 後端在 channelGroup API 回傳的頂層欄位，列出此渠道支援的影片編碼
  // （例如 outdoor >= H264，非 outdoor 為 []）。wrapChannelGroup 會原樣帶過頂層未知欄位。
  allowedVideoCodecs?: string[];
};

export type ChannelGroupListDTO = ChannelGroupDTO;

// ECOPD-5572 / ECOPD-5701: 下面四個停用相關欄位被 Omit 是刻意的。
// 這個型別會被渠道設定表單整包 PUT 回後端，留著的話 sysadmin 每存一次就把自停狀態與
// 停用理由洗掉 —— 而停用後那個畫面仍要能用，等於天天會被存。
// `wrapForServer` 在 runtime 再 omit 一次，兩層都擋。
export type ChannelGroupFormData = Omit<
  ChannelGroupDTO,
  | 'id'
  | 'campaignAdTypeSettings'
  | 'pmaxArchived'
  | 'effectiveArchived'
  // ECOPD-5701: 理由同理。
  | 'pmaxArchiveReason'
  | 'rmaxArchiveReason'
> & {
  campaignAdTypeSettings: CampaignAdTypeSettingFormData[];
  // ECOPD-5581: 純前端的表單欄位，FileInput 要的 { file, url } 形狀。
  // 由 epaperDefaultImageUrl 展開而來，送出前換回 URL 字串並從 payload omit 掉。
  epaperDefaultImageData?: ImageFieldData;
  // 品牌的兩張地圖圖示，同樣是表單專用的 { file, url }。送出時換回
  // property.addonProps.brand 的兩個 URL，本身不進 payload。
  brandFilteredIconData?: ImageFieldData;
  brandNormalIconData?: ImageFieldData;
};

export type NameI18n = {
  ja: string;
  en: string;
  'zh-TW': string;
};

export enum ScreenDisplayType {
  SINGLE = 'SINGLE',
  MULTI_SPLICED = 'MULTI_SPLICED',
  SYNCHRONIZED_PLAYBACK = 'SYNCHRONIZED_PLAYBACK'
}

export const PIC_RELATED_CHANNELS = [2, 4];
